/* eslint-disable no-invalid-this */
import { clone, difference, isEqual } from 'lodash';
import idx from '../lib/idx';

/** model system main blue print */
export abstract class Model {
  // I dont like this being public but unless I put the model and Property inside the service I don't have much choice.
  public static attributes; // dont define me!
  public static _records: any;

  public changes = {};
  public _values = {};
  public _rawApiData = null;
  public _constructed: boolean;

  constructor() {
    Object.defineProperty(this, 'service', { enumerable: false });

    // Used to determine whether model is a full or partial model
    Object.defineProperty(this, '_partial', {
      value: false,
      enumerable: false,
      configurable: true,
      writable: true
    });

    // Stored API data used to saturate a partial model
    Object.defineProperty(this, '_rawApiData', {
      enumerable: false,
      configurable: true,
      writable: true
    });
  }

  public static get idAttribute(): string {
    return null;
  }

  public isNew(): boolean {
    return !this[(<typeof Model>this.constructor).idAttribute];
  }

  public clearChanges(attr: string = null): void {
    const _clear = (key, val) => {
      if (val instanceof Array && val.length) {
        for (const i of Array.from(val)) {
          idx(i, (o) => o.clearChanges());
        }
      } else {
        idx(val, (o1) => o1.clearChanges());
      }

      return delete this.changes[key];
    };

    if (attr) {
      _clear(attr, this._values[attr]);
    } else {
      for (let i of Object.keys(this._values)) {
        const val = this._values[i];
        _clear(i, val);
      }
    }

    return null;
  }

  public remove(): void {
    let records;
    debug('model', 'remove', (records = (<typeof Model>this.constructor)._records));
    const index = (() => {
      for (const i of Object.keys(records)) {
        const r = records[i];
        if (
          r[(<typeof Model>this.constructor).idAttribute] ===
          this[(<typeof Model>this.constructor).idAttribute]
        ) {
          return i;
        }
      }

      return -1;
    })();
    // @ts-ignore
    if (!(index >= 0)) {
      return;
    }
    (<typeof Model>this.constructor)._records.splice(index, 1);
  }

  // reset model changes (revert to previous values)
  public reset(): void {
    let val;
    debug((<typeof Model>this.constructor).name, 'reset');
    for (const key of Object.keys(this.changes)) {
      val = this.changes[key];
      this._values[key] = val;
      delete this.changes[key];
    }

    // reset child models, too
    if (this.isModified()) {
      let key;
      for (key of Object.keys(this._values)) {
        val = this._values[key];
        if (val instanceof Array && val.length) {
          for (const i of Array.from(val)) {
            idx(i, (o) => o.reset());
          }
        } else {
          idx(val, (o1) => o1.reset());
        }
      }
    }

    return null;
  }

  // mark an attribute as modified
  public markModified(attr: string): void {
    return (this.changes[attr] = null);
  }

  // unmark an attribute as modified
  public unmarkModified(attr: string): void {
    if (this.changes != null) {
      delete this.changes[attr];
    }
  }

  // check if model has unsaved changes
  public isModified(attribute: string = null, doLog: boolean = false): boolean {
    // check for changes on individual attribute, if passed in
    let keys;
    let val;
    if (attribute) {
      val = this._values[attribute];
      if (val instanceof Array) {
        for (const obj of Array.from(val)) {
          if (idx(obj, (o) => o.isModified())) {
            return true;
          }
        }
      }

      return idx(val, (o1) => o1.isModified()) || Object.keys(this.changes).includes(attribute);
    }

    // otherwise, check for any changes on the model
    for (const key of Object.keys(this._values)) {
      val = this._values[key];
      if (val instanceof Array) {
        for (const i of Array.from(val)) {
          if (idx(i, (o2) => o2.isModified())) {
            debug(['model', (<typeof Model>this.constructor).name], 'modified', key);
            if (doLog) {
              log(['model', (<typeof Model>this.constructor).name], 'modified', key);
            }

            return true;
          }
        }
      } else if (idx(val, (o3) => o3.isModified())) {
        debug(['model', (<typeof Model>this.constructor).name], 'modified', key);
        if (doLog) {
          log(['model', (<typeof Model>this.constructor).name], 'modified', key);
        }

        return true;
      }
    }

    if ((keys = Object.keys(this.changes || {})).length > 0) {
      debug(['model', (<typeof Model>this.constructor).name], 'modified', keys);
      if (doLog) {
        log(['model', (<typeof Model>this.constructor).name], 'modified', keys);
      }

      return true;
    }

    return false;
  }

  public setter(key: string, obj: any): (val: any) => any {
    return function (val: any, _constructed: boolean = false): void {
      let constructed = _constructed;
      let value = val;

      if (constructed == null) {
        constructed = this._constructed || false;
      }
      if (typeof obj.set === 'function') {
        value = obj.set.apply(this, [value]);
      }

      if (obj.validate != null && typeof obj.validate === 'function') {
        let res = obj.validate(value);
        if (res !== true) {
          throw Error(res);
        }
      }

      if (constructed) {
        if (!isEqual(value, this._values[key])) {
          if (!(key in Object.keys(this.changes))) {
            if (obj.track !== false) {
              this.changes[key] = this._values[key];
            }
          }
        }

        // check to see if array is back to original members
        // and don't mark that as changed (unless order is significant)
        if (
          this.changes[key] instanceof Array &&
          difference(this.changes[key], value).length === 0 &&
          this.changes[key].length === value.length
        ) {
          if (!obj.orderSignificant) {
            delete this.changes[key];
          }
        }
        if (key in Object.keys(this.changes) && isEqual(value, this.changes[key])) {
          delete this.changes[key];
        }
      }

      this._values[key] = value;
    };
  }

  public getter(key: string, obj: any): () => any {
    return function (): any {
      let val = this._values[key];
      if (obj.get != null && typeof obj.get === 'function') {
        val = obj.get.apply(this, [val]);
      }

      // override array mutator methods to trigger custom setter
      if (val instanceof Array) {
        ['pop', 'push', 'shift', 'unshift', 'sort', 'reverse', 'splice'].forEach((method) => {
          val[method] = (...params: any[]) => {
            const clonedArray = [...this._values[key]];
            const returnValue = Array.prototype[method].apply(clonedArray, params);
            this[key] = clonedArray;

            return returnValue;
          };
        });
      }

      return val;
    };
  }

  public toString(): string {
    return JSON.stringify(this.toObject());
  }

  public toObject(): any {
    const obj = {};
    for (const key of Object.keys((<typeof Model>this.constructor).attributes)) {
      if (this[key] instanceof Array) {
        obj[key] = [];
        for (const o of this[key]) {
          obj[key].push(idx(o, (o1) => o1.toObject()) || o);
        }
      } else {
        obj[key] = idx(this[key], (o2) => o2.toObject()) || this[key];
      }
    }

    return obj;
  }

  protected isModel(type: any): boolean {
    try {
      return type instanceof Model;
    } catch (e) {
      log('isModel error', e);

      return false;
    }
  }
}
